import { DocumentType } from "src/types/graphql";
import { fieldOptions } from "src/utils/textTranslation";
import { IntakeDealPayload } from "src/api/service/crm";

interface BuildIntakeDealParams {
  quotationRequestId: string;
  contact: {
    name: string;
    email: { address: string };
    phone: { number: string };
  };
  estimatedProductPrice: number | null;
  requirement: { notes: string; expectedDeliveryDate: Date | null };
  source: { type: DocumentType | "" };
  works: { sourceLanguage: string; targetLanguages: string[] };
  uploadFile: { name: string };
}

const FIXED_FIELDS = {
  source: "website",
  serviceLine: "VO",
  leadSourceLabel: "텍스트 견적",
  translationTypeLabel: "텍스트 번역",
} as const;

const getFieldLabel = (type: DocumentType | ""): string =>
  fieldOptions.find((field) => field.key === type)?.option ?? "";

// 로컬 시각 기준 YYYY-MM-DD (toISOString의 UTC 변환으로 날짜가 밀리는 것 방지)
const formatDeliveryDate = (date: Date | null): string => {
  if (!date) {
    return "상관없음";
  }
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// PDF 예시 형식의 importRemarks 조립
const buildImportRemarks = (params: BuildIntakeDealParams): string => {
  const lines: string[] = [];

  const fieldLabel = getFieldLabel(params.source.type);
  if (fieldLabel) {
    lines.push(`번역 분야: ${fieldLabel}`);
  }

  const isFileMode = Boolean(params.uploadFile.name);
  lines.push(`입력 방식: ${isFileMode ? "파일첨부" : "텍스트"}`);
  if (isFileMode) {
    lines.push(`파일명: ${params.uploadFile.name}`);
  }

  lines.push(
    `희망 납품일: ${formatDeliveryDate(params.requirement.expectedDeliveryDate)}`,
  );

  return lines.join("\n");
};

export const buildIntakeDealPayload = (
  params: BuildIntakeDealParams,
): IntakeDealPayload => {
  const { quotationRequestId, contact, estimatedProductPrice, requirement, works } =
    params;

  return {
    ...FIXED_FIELDS,
    submissionId: `text-translation-${quotationRequestId}`,
    contactName: contact.name,
    contactEmail: contact.email.address,
    contactPhone: contact.phone.number.replace(/-/g, ""),
    languagePair: `${works.sourceLanguage} → ${works.targetLanguages.join(", ")}`,
    amount: estimatedProductPrice ?? 0,
    intakeResponse: requirement.notes,
    importRemarks: buildImportRemarks(params),
  };
};
